import { PlusOutlined } from '@ant-design/icons'
import { Button, PageHeader, EmptyState, NoDataIcon, Search } from '@lmc/ui-kit'
import styled from 'styled-components'
import useDailyTasks from './hooks/useDailyTasks'
import { Spinner } from 'components/generic'
import dayjs, { Dayjs } from 'dayjs'
import { ChangeEventHandler, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import DayCard from './components/DayCard/DayCard'
import TaskFilters, {
  TaskFiltersValue,
} from './components/TaskFilters/TaskFilters'
import { RangeValue } from 'types/shared'
import actions from 'actions'
import DayNavigation from './components/DayNavigation/DayNavigation'
import SortDailyTasks from './components/SortDailyTasks/SortDailyTasks'
import { debounce } from 'lodash'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import useToDosFilterParams from './hooks/useFilterToDosParams'
import { Alert } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import { ADD_TODO_HASH_ROUTE, initialFiltersValue } from '../NewManager/utils'
import { UserRole } from '@lmc/ts-common'
import { useRoleAccess } from 'components/AccessControl'

const HELP_ARTICLE_URL =
  'https://support.logmycare.co.uk/en/articles/1571162-how-can-i-see-and-edit-the-status-of-today-s-to-do-s-in-one-place'

const SHOWN_FUTURE_TASKS_HELP_URL =
  'https://support.logmycare.co.uk/en/articles/8348676-re-generating-to-dos'

const NewDashboard = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showDayNavigation, setShowDayNavigation] = useState(true)
  const [sortOrder, setSortOrder] = useState<'-1' | '1'>('-1')

  const location = useLocation()

  const [filterParams, setFilterParams] =
    useToDosFilterParams(initialFiltersValue)

  const {
    isLoading: isQueryLoading,
    days,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchedAfterMount,
  } = useDailyTasks({
    ...filterParams,
    sortOrder,
  })

  const isLoading = isQueryLoading || !isFetchedAfterMount

  const { t } = useTranslation(NAMESPACE.TODOS)

  const handleNextDay = () => {
    setFilterParams((prevFilterParams) => ({
      ...prevFilterParams,
      rangePicker: prevFilterParams.rangePicker?.map((dateValue) =>
        dateValue?.add(1, 'day'),
      ) as RangeValue<Dayjs>,
    }))
    window.scrollTo(0, 0)
  }

  const onFilterChange = (value: TaskFiltersValue) => {
    const newValue = { ...value }

    if (newValue.rangePicker) {
      if (
        dayjs(newValue.rangePicker[0])?.toString() !==
        dayjs(newValue.rangePicker[1])?.toString()
      ) {
        setShowDayNavigation(false)
      } else {
        setShowDayNavigation(true)
      }
    } else {
      newValue.rangePicker = initialFiltersValue.rangePicker

      setShowDayNavigation(true)
    }

    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...newValue,
    }))
  }

  const handlePreviousDay = () => {
    setFilterParams((prevFilterParams) => ({
      ...prevFilterParams,
      rangePicker: prevFilterParams.rangePicker?.map((dateValue) =>
        dateValue?.subtract(1, 'day'),
      ) as RangeValue<Dayjs>,
    }))
    window.scrollTo(0, 0)
  }

  const handleOpenModal = () => {
    window.scrollTo(0, 0)
    dispatch(
      actions.openModal({
        modalId: 'createTask',
        data: {
          onComplete: () => {
            refetch()
            navigate(location.pathname)
          },
        },
      }),
    )
    dispatch(actions.addForm({ formId: 'createTask' }))
  }

  useEffect(() => {
    if (location.hash.includes(ADD_TODO_HASH_ROUTE)) {
      handleOpenModal()
    }
  }, [location.hash])

  const debouncedSaved = debounce(() => {
    fetchNextPage()
  }, 512)
  const debouncedSearch = debounce((searchValue: string) => {
    setFilterParams((prev) => ({
      ...prev,
      search: searchValue,
    }))
  }, 1024)
  const onSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    debouncedSearch(event.target.value.trim())
  }
  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement
      const threshold = 50

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        debouncedSaved()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [days])

  const maxGeneratedTasksDate = dayjs().add(1, 'month')
  const showUngeneratedTasksMessage = filterParams.rangePicker?.[1]?.isAfter(
    maxGeneratedTasksDate,
  )
  const hasRolePermission = useRoleAccess(
    [UserRole.seniorCarer, UserRole.careHomeAdmin],
    [
      {
        i: 'create',
        this: 'TaskManagement',
      },
    ],
  )

  const hasResults = !!days.length

  return (
    <Wrapper>
      <PageHeader
        title={
          <TitleContainer>{t(toDosConstants.DASHBOARD_TITLE)}</TitleContainer>
        }
        moreInfoLink={HELP_ARTICLE_URL}
      >
        <RightHeaderWrapper>
          <Search
            placeholder={t(toDosConstants.SEARCH_BY_TITLE)}
            onChange={onSearch}
          />
          <ButtonHeaderStyled
            onClick={handleOpenModal}
            variant="primary"
            icon={<PlusOutlined />}
            data-cy="btn-add-todo"
            disabledByPermission={!hasRolePermission}
          >
            {t(toDosConstants.ADD_A_TODO)}
          </ButtonHeaderStyled>
        </RightHeaderWrapper>
      </PageHeader>
      <div>
        <TaskFilters onChange={onFilterChange} value={filterParams} />
        {showUngeneratedTasksMessage && (
          <StyledAlert
            showIcon
            message={
              <Text level={2} weight="medium">
                {t(toDosConstants.RECURRING_DISCLAIMER)}
                <Link to={SHOWN_FUTURE_TASKS_HELP_URL}>
                  {t(toDosConstants.HERE).concat('.')}
                </Link>
              </Text>
            }
            closable
          />
        )}
      </div>
      {!showDayNavigation && <SortDailyTasks setSortOrder={setSortOrder} />}
      {isLoading ? (
        <Spinner />
      ) : (
        <DashboardContent>
          {!isLoading && hasResults && (
            <DaysWrapper>
              {days?.map((day) => (
                <DayCard
                  day={day}
                  key={day.date.toString()}
                  filterParams={filterParams}
                  refetchDailyTasks={refetch}
                />
              ))}
            </DaysWrapper>
          )}
          {!isLoading && !hasResults && (
            <EmptyState
              icon={<NoDataIcon />}
              title={t(toDosConstants.NO_TASKS)}
              description={t(toDosConstants.NO_TASKS_DESCRIPTION)}
            />
          )}

          {showDayNavigation && (
            <DayNavigation
              handlePreviousDay={handlePreviousDay}
              handleNextDay={handleNextDay}
            />
          )}
        </DashboardContent>
      )}
      {hasNextPage && <Spinner />}
    </Wrapper>
  )
}

const GAP_BETWEEN_ELEMENTS = 24

const StyledAlert = styled(Alert)`
  margin-top: 12px;
  color: ${(props) => props.theme.color.neutral.color4};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GAP_BETWEEN_ELEMENTS}px;
  max-width: 1080px;
  padding: 24px 24px 50px;
  margin: 0 auto;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`

const DaysWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const RightHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ButtonHeaderStyled = styled(Button)`
  white-space: nowrap;
`

const DashboardContent = styled.div``

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

export default NewDashboard
